import { useState, useEffect } from 'react';
import { Sort } from 'src/icons/Sort.icon';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, Menu, Select, Button } from 'antd';
import {
  ActionMenuIcon,
  BinIcon,
  EditAppointmentIcon,
  PlusIcon,
} from 'src/icons';
import { SuperAdminTable } from '../../../baseComponents/Table';
import {
  listAgentsAction,
  getAgentAction,
} from '../../../../store/actions/agents.action';
import AddNewAgent from './AddNewAgent';
import EditAgent from './EditAgent';
import DeleteAgent from './DeleteAgent';
import OrganizationFilter from '../OrganizationFilter';
import { getFromStorage } from 'src/lib/storage';
import organizationEventEmitter from 'src/utilities/organizationEmmiter';
import { getAllActiveOrganizationAction } from 'src/modules/SuperAdmin/store/actions/organization.action';

export const AgentsPage = () => {
  const { agentsList, agentsLoading } = useSelector(
    (state) => state?.agentsState
  );
  const { currentPractitioner } = useSelector(
    (state) => state?.practitionerState
  );
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isfilterOn, setIsFilterOn] = useState(false);

  const [editingAgentId, setEditingAgentId] = useState(null);
  const [deletingAgentId, setDeletingAgentId] = useState(null);

  const [filter, setFilter] = useState({
    product: null,
  });

  // Organization filtering state
  const [selectedOrganizationUUID, setSelectedOrganizationUUID] = useState(
    () => {
      const savedOrg = getFromStorage('superAdminSelectedOrganization');
      const parsedSavedOrg = savedOrg ? JSON.parse(savedOrg) : null;
      return parsedSavedOrg?.org_uuid || currentPractitioner?.org_uuid || null;
    }
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedOrganizationUUID) {
      dispatch(listAgentsAction(selectedOrganizationUUID, filter));
      dispatch(getAllActiveOrganizationAction());
    }
  }, [dispatch, selectedOrganizationUUID]);

  // Listen for organization changes
  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      (newOrg) => {
        setSelectedOrganizationUUID(newOrg?.org_uuid);
      }
    );
    return () => unsubscribe();
  }, []);

  const handleEditAgent = (agentId) => {
    setEditingAgentId(agentId);
    setEditModalOpen(true);
  };

  const handleDeleteAgent = (agentId) => {
    setDeletingAgentId(agentId);
    setDeleteModalOpen(true);
  };

  const menuOptions = (id, record) => {
    return (
      <Menu
        onClick={({ key }) => {
          if (key === 'editAgent') {
            handleEditAgent(record?.agent_id);
          }

          if (key === 'deleteAgent') {
            handleDeleteAgent(record?.agent_id);
          }
        }}
      >
        <Menu.Item key="editAgent">
          <div className="dropdown-text flex items-center space-x-2">
            <EditAppointmentIcon /> <span>Edit agent</span>
          </div>
        </Menu.Item>
        <Menu.Item key="deleteAgent">
          <div className="dropdown-text flex items-center space-x-2">
            <BinIcon /> <span>Remove agent</span>
          </div>
        </Menu.Item>
      </Menu>
    );
  };

  const nameSorter = (a, b) => {
    const nameA = a.name || '';
    const nameB = b.name || '';
    return nameA.localeCompare(nameB);
  };

  const productSorter = (a, b) => {
    const productA = a.product || '';
    const productB = b.product || '';
    return productA.localeCompare(productB);
  };

  const statusSorter = (a, b) => {
    const statusA = a.status || '';
    const statusB = b.status || '';
    return statusA.localeCompare(statusB);
  };

  const columns = [
    {
      title: (
        <span className="font-[700]">
          {' '}
          Agent Name{' '}
          {agentsList?.agents?.length > 0 && (
            <Sort className={'sort-icon-doctor'} />
          )}
        </span>
      ),
      dataIndex: 'name',
      key: 'name',
      sorter: agentsList?.agents?.length > 0 ? nameSorter : false,
    },
    {
      title: (
        <span className="font-[700]">
          {' '}
          Product{' '}
          {agentsList?.agents?.length > 0 && (
            <Sort className={'sort-icon-doctor'} />
          )}
        </span>
      ),
      dataIndex: 'product',
      key: 'product',
      sorter: agentsList?.agents?.length > 0 ? productSorter : false,
    },
    {
      title: <span className="font-[700]"> External ID </span>,
      dataIndex: 'external_id',
      key: 'external_id',
      render: (text) => (
        <div className="max-w-xs truncate" title={text}>
          {text || 'N/A'}
        </div>
      ),
    },
    {
      title: '',
      key: 'actions',
      dataIndex: 'actions',
      render: (text, record) => {
        return (
          <Dropdown
            className=""
            overlay={menuOptions(record?.agent_id, record)}
          >
            <a
              className=""
              onClick={(e) => {
                e.preventDefault();
              }}
            >
              <ActionMenuIcon />
            </a>
          </Dropdown>
        );
      },
    },
  ];

  const initialFilterValue = {
    status: null,
    product: null,
  };

  const [filterValue, setFilterValue] = useState(initialFilterValue);

  const filterHandler = (type, value) => {
    let updatedFilterValue = { ...filterValue };
    updatedFilterValue[type] = value;
    setFilterValue(updatedFilterValue);

    if (selectedOrganizationUUID) {
      dispatch(listAgentsAction(selectedOrganizationUUID, updatedFilterValue));
    }
  };

  const resetFilter = () => {
    setFilterValue(initialFilterValue);
    setIsFilterOn(false);
    if (selectedOrganizationUUID) {
      dispatch(listAgentsAction(selectedOrganizationUUID));
    }
  };

  return (
    <div className="page-wrapper">
      <div className="flex justify-between items-center py-3 px-1">
        <div className="flex items-center gap-4">
          <OrganizationFilter shouldShowSuperAdmin={true} />
          <div className="bg-white shadow-sm rounded-xl w-48 flex items-center">
            <Select
              placeholder="All Products"
              bordered={false}
              className="border-none font-sans h-10 flex items-center focus:outline-none w-full"
              style={{
                placeholder: {
                  fontFamily: 'Poppins',
                  color: '#667085',
                },
              }}
              value={filterValue.product}
              onChange={(value) => filterHandler('product', value)}
              allowClear
            >
              <Select.Option value="frontdesk">Frontdesk</Select.Option>
              <Select.Option value="phone-triage">Phone Triage</Select.Option>
            </Select>
          </div>
          {filterValue.product && (
            <Button
              type="link"
              onClick={resetFilter}
              className="text-blue-600 p-0"
            >
              Clear Filters
            </Button>
          )}
        </div>
        <Button
          type="primary"
          onClick={() => setAgentModalOpen(true)}
          disabled={!selectedOrganizationUUID}
          className="flex items-center gap-2"
        >
          <PlusIcon />
          Add New Agent
        </Button>
      </div>
      <div className="mt-5 ant-table rounded-xl">
        <SuperAdminTable
          className={'appointment-page-table'}
          columns={columns}
          data={agentsList?.agents}
          loading={agentsLoading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} agents`,
          }}
        />
      </div>

      <AddNewAgent
        isOpen={agentModalOpen}
        setIsOpen={setAgentModalOpen}
        filterValue={filterValue}
        orgUuid={selectedOrganizationUUID}
      />

      <EditAgent
        isOpen={editModalOpen}
        setIsOpen={setEditModalOpen}
        agentId={editingAgentId}
        isfilterOn={isfilterOn}
        filterValue={filterValue}
        orgUuid={selectedOrganizationUUID}
      />

      <DeleteAgent
        isOpen={deleteModalOpen}
        setIsOpen={setDeleteModalOpen}
        filterValue={filterValue}
        agentId={deletingAgentId}
        orgUuid={selectedOrganizationUUID}
      />
    </div>
  );
};

export default AgentsPage;
